import {
  authorizedShopReviewAnalysisSchema,
  imageGenerationResultSchema,
  imageProviderSchema,
  imageProviderTestResultSchema,
  mainImageLibraryItemSchema,
  publicProductReviewAnalysisSchema,
  reviewLibraryItemSchema,
  type AuthorizedShopReviewAnalysis,
  type ImageGenerationRequest,
  type ImageGenerationResult,
  type ImageProvider,
  type ImageProviderId,
  type ImageProviderInput,
  type ImageProviderTestResult,
  type MainImageLibraryInput,
  type MainImageLibraryItem,
  type PublicProductReviewAnalysis,
  type PublicProductReviewAnalysisInput,
  type ReviewLibraryInput,
  type ReviewLibraryItem,
  type ReviewTextGenerationRequest,
  type ReviewTextGenerationResult,
  reviewTextGenerationResultSchema
} from '@jianze/contracts';
import { z } from 'zod';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';
const imageResponseInterruptedMessage = '图片接口连接中断，未收到完整结果。本张图片不会自动重试，请点击“继续生成未完成图片”重试。';

export function resolveImageApiBase(apiBase: string, configuredBase: string | undefined, isDevelopment: boolean) {
  const explicitBase = configuredBase?.trim();
  if (explicitBase) return explicitBase;
  return isDevelopment && apiBase === '/api' ? 'http://127.0.0.1:3101' : apiBase;
}

const IMAGE_API_BASE = resolveImageApiBase(API_BASE, import.meta.env.VITE_IMAGE_API_BASE_URL, import.meta.env.DEV);
const clipboardImageImportSchema = z.object({
  dataUrl: z.string().startsWith('data:image/'),
  mimeType: z.enum(['image/png', 'image/jpeg', 'image/webp']),
  size: z.number().int().positive(),
  name: z.string().min(1)
});

interface RequestOptions {
  baseUrl?: string;
  interruptedMessage?: string;
}

export async function parseApiResponse<T>(response: Response, schema: z.ZodType<T>, interruptedMessage = '接口返回不完整，请稍后重试。'): Promise<T> {
  const body = await response.text();
  if (!body.trim()) throw new Error(interruptedMessage);

  let payload: unknown;
  try {
    payload = JSON.parse(body) as unknown;
  } catch {
    throw new Error(interruptedMessage);
  }

  if (!response.ok) {
    const parsed = z.object({ error: z.string() }).safeParse(payload);
    throw new Error(parsed.success ? parsed.data.error : `API request failed: ${response.status}`);
  }
  return z.object({ data: schema, requestId: z.string() }).parse(payload).data;
}

async function request<T>(path: string, schema: z.ZodType<T>, init?: RequestInit, options: RequestOptions = {}): Promise<T> {
  const interruptedMessage = options.interruptedMessage ?? '接口连接中断，请稍后重试。';
  let response: Response;
  try {
    response = await fetch(`${options.baseUrl ?? API_BASE}${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', ...init?.headers }
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    throw new Error(interruptedMessage);
  }
  return parseApiResponse(response, schema, interruptedMessage);
}

export const dashboardApi = {
  imageProviders: () => request<ImageProvider[]>('/v1/image-providers', z.array(imageProviderSchema)),
  saveImageProvider: (provider: ImageProviderId, input: ImageProviderInput) => request<ImageProvider>(
    `/v1/image-providers/${provider}`,
    imageProviderSchema,
    { method: 'PUT', body: JSON.stringify(input) }
  ),
  testImageProvider: (provider: ImageProviderId, operation: 'generation' | 'edit' = 'generation') => request<ImageProviderTestResult>(
    `/v1/image-providers/${provider}/test`,
    imageProviderTestResultSchema,
    { method: 'POST', body: JSON.stringify({ operation }) }
  ),
  generateImages: (input: ImageGenerationRequest, signal?: AbortSignal) => request<ImageGenerationResult>(
    '/v1/images/generate',
    imageGenerationResultSchema,
    { method: 'POST', body: JSON.stringify(input), ...(signal ? { signal } : {}) },
    { baseUrl: IMAGE_API_BASE, interruptedMessage: imageResponseInterruptedMessage }
  ),
  importClipboardImage: (url: string) => request('/v1/clipboard-images/import', clipboardImageImportSchema, {
    method: 'POST',
    body: JSON.stringify({ url })
  }),
  analyzeAuthorizedShopReviews: () => request<AuthorizedShopReviewAnalysis>(
    '/v1/pdd-reviews/analyze',
    authorizedShopReviewAnalysisSchema,
    { method: 'POST', body: '{}' }
  ),
  analyzePublicProductReviews: (input: PublicProductReviewAnalysisInput) => request<PublicProductReviewAnalysis>(
    '/v1/pdd-public-reviews/analyze',
    publicProductReviewAnalysisSchema,
    { method: 'POST', body: JSON.stringify(input) }
  ),
  reviewLibrary: () => request<ReviewLibraryItem[]>('/v1/review-library', z.array(reviewLibraryItemSchema)),
  generateReviewTexts: (input: ReviewTextGenerationRequest) => request<ReviewTextGenerationResult>(
    '/v1/reviews/generate-text', reviewTextGenerationResultSchema,
    { method: 'POST', body: JSON.stringify(input) }
  ),
  saveReviewLibrary: (input: ReviewLibraryInput) => request<ReviewLibraryItem>(
    '/v1/review-library',
    reviewLibraryItemSchema,
    { method: 'POST', body: JSON.stringify(input) }
  ),
  deleteReviewLibrary: (id: string) => request<{ removed: true }>(
    `/v1/review-library/${encodeURIComponent(id)}`,
    z.object({ removed: z.literal(true) }),
    { method: 'DELETE' }
  ),
  mainImageLibrary: () => request<MainImageLibraryItem[]>('/v1/main-image-library', z.array(mainImageLibraryItemSchema)),
  saveMainImageLibrary: (input: MainImageLibraryInput) => request<MainImageLibraryItem>(
    '/v1/main-image-library',
    mainImageLibraryItemSchema,
    { method: 'POST', body: JSON.stringify(input) }
  ),
  deleteMainImageLibrary: (id: string) => request<{ removed: true }>(
    `/v1/main-image-library/${encodeURIComponent(id)}`,
    z.object({ removed: z.literal(true) }),
    { method: 'DELETE' }
  )
};

export interface AuthUser {
  id: number;
  username: string;
  phone: string;
  displayName: string;
  role: 'admin' | 'user';
  status: 'pending' | 'active' | 'rejected' | 'disabled';
  reason: string;
  mustChangePassword: boolean;
  lastLoginAt: number | null;
  createdAt: number;
}

export interface CreatedAuthUser {
  user: AuthUser;
  temporaryPassword: string;
}

const authServiceUnavailableMessage = '账号服务暂时不可用，请稍后刷新重试。';

export async function parseAuthApiResponse(response: Response): Promise<unknown> {
  const body = await response.text();
  if (!body.trim()) throw new Error(authServiceUnavailableMessage);

  let payload: unknown;
  try {
    payload = JSON.parse(body) as unknown;
  } catch {
    throw new Error(authServiceUnavailableMessage);
  }

  if (typeof payload !== 'object' || payload === null) {
    throw new Error(authServiceUnavailableMessage);
  }

  const envelope = payload as { data?: unknown; error?: unknown };
  if (!response.ok) {
    throw new Error(typeof envelope.error === 'string' ? envelope.error : authServiceUnavailableMessage);
  }
  if (!Object.prototype.hasOwnProperty.call(envelope, 'data')) {
    throw new Error(authServiceUnavailableMessage);
  }
  return envelope.data;
}

async function authRequest(path: string, init?: RequestInit) {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', ...init?.headers }
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    throw new Error(authServiceUnavailableMessage);
  }
  return parseAuthApiResponse(response);
}
export const authApi = {
  me: () => authRequest('/auth/me') as Promise<AuthUser>,
  login: (phone: string, password: string) => authRequest('/auth/login', { method: 'POST', body: JSON.stringify({ phone, password }) }) as Promise<AuthUser>,
  logout: () => authRequest('/auth/logout', { method: 'POST', body: '{}' }),
  changePassword: (currentPassword: string, newPassword: string) => authRequest('/auth/password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }) as Promise<{ changed: true }>,
  users: () => authRequest('/auth/users') as Promise<AuthUser[]>,
  createUser: (input: { phone: string; displayName: string; password?: string }) => authRequest('/auth/users', { method: 'POST', body: JSON.stringify(input) }) as Promise<CreatedAuthUser>,
  updateUser: (id: number, input: { phone: string; displayName: string }) => authRequest(`/auth/users/${id}`, { method: 'PATCH', body: JSON.stringify(input) }) as Promise<AuthUser>,
  deleteUser: (id: number) => authRequest(`/auth/users/${id}`, { method: 'DELETE' }) as Promise<{ removed: true }>,
  updateAdminPhone: (phone: string, currentPassword: string) => authRequest('/auth/admin-phone', { method: 'PATCH', body: JSON.stringify({ phone, currentPassword }) }) as Promise<AuthUser>,
  setStatus: (id: number, status: AuthUser['status']) => authRequest(`/auth/users/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) }) as Promise<AuthUser>,
  resetPassword: (id: number) => authRequest(`/auth/users/${id}/reset-password`, { method: 'POST', body: '{}' }) as Promise<CreatedAuthUser>,
  revokeSessions: (id: number) => authRequest(`/auth/users/${id}/logout`, { method: 'POST', body: '{}' }) as Promise<{ revoked: number }>
};
